import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import BottomFixedContainer from '@/components/molecule/BottomFixedContainer';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import { BUTTON_CONST } from '@/src/constants/buttons';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import DetailSection, {
  DetailSectionContent,
} from '@/components/organism/DetailSection';
import { DetailSctionRenderer } from '@/components/organism/DetailSection/renderer';
import { getCustomersByIdAPI } from '@/src/apis/rest/customers';
import { NAV_CONST } from '@/src/constants/nav';
import { CustomersDto, CustomerStatus } from '@/src/models/CustomersDto';
import React, { useCallback, useState } from 'react';
import SectionCard from '@/components/molecule/SectionCard';
import StatusManageDialog from '@/src/pages/Customer/components/StatusManageDialog';
import dayjs from 'dayjs';
import CreditManageDialog from '@/src/pages/Customer/components/CreditManageDialog';
import CustomerDetailTabTable from '@/src/pages/Customer/components/CustomerDetailTabTable';
import { UNIT_STRINGS } from '@/src/constants/unit';
import PATH from '@/src/routes/path';
import {
  CUSTOMER_STATUS_OPTIONS,
  OPTIONS_CONST,
} from '@/src/constants/options';
import Typo from '@/components/atom/Typo';
import { formatter } from '@/src/utils/formatter';

const BACK_PATH = PATH.BUSINESS_CUSTOMER_LIST;

function BusinessCustomerDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);

  const { data: customerData, refetch: refetchCustomerData } = useQuery({
    queryKey: ['customers', 'business', id],
    queryFn: () => getCustomersByIdAPI(Number(id)),
    enabled: !!id,
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  const normalContents = useCallback(() => {
    return [
      [
        {
          key: 'createdAt',
          lable: '가입 일시',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'signupSourceName',
          lable: '가입 경로',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.signupSource === null
              ? '에코야얼스'
              : data?.signupSource?.partnershipCompany?.name || '-',
        },
        {
          key: 'nick',
          lable: '닉네임',
          valueSetter: (data: CustomersDto | undefined) => data?.nick || '-',
        },
        {
          key: 'aId',
          lable: '회원 아이디',
          valueSetter: (data: CustomersDto | undefined) => data?.aId || '-',
        },
        {
          key: 'name',
          lable: '회원 이름',
          valueSetter: (data: CustomersDto | undefined) => data?.name || '-',
        },
        {
          key: 'businessName',
          lable: '사업체 명',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.customerBusinessInfo.businessName || '-',
        },
        {
          key: 'businessRegisterNumber',
          lable: '사업자등록번호',
          valueSetter: (data: CustomersDto | undefined) =>
            formatter.BUSINESS_NUMBER(
              data?.customerBusinessInfo.businessRegisterNumber || '',
            ) || '-',
        },
        {
          key: 'openDate',
          lable: '개업일',
          valueSetter: (data: CustomersDto | undefined) =>
            dayjs(data?.customerBusinessInfo.openDate).format('YYYY-MM-DD') ||
            '-',
        },
        {
          key: 'reportCnt',
          lable: '블라인드 수',
          valueSetter: (data: CustomersDto | undefined) => {
            const commentCount = data?.communityCommentReportCnt || 0;
            const postingCount = data?.communityPostReportCnt || 0;
            return `${(commentCount + postingCount).toLocaleString()}회`;
          },
        },
      ],
      [
        {
          key: 'status',
          lable: '회원 상태',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.status || 'normal',
          render: (value: string) => {
            const status: CustomerStatus = value as CustomerStatus;
            return (
              <>
                <span>{CUSTOMER_STATUS_OPTIONS[status]}</span>
                {value !== 'withdrawal' && (
                  <Button
                    buttonColor="secondary"
                    buttonSize="xSmall"
                    onClick={() => setIsStatusDialogOpen(true)}
                  >
                    회원 상태 관리
                  </Button>
                )}
              </>
            );
          },
        },
        {
          key: 'credit',
          lable: `보유 크레딧(${UNIT_STRINGS.CREDIT})`,
          valueSetter: (data: CustomersDto | undefined) =>
            data?.credit?.currentCredit.toLocaleString() || '0',
          render: (credit: string) => {
            return (
              <>
                <span>{`${credit}`}</span>
                {customerData?.status !== 'withdrawal' && (
                  <Button
                    buttonColor="secondary"
                    buttonSize="xSmall"
                    onClick={() => setIsCreditDialogOpen(true)}
                  >
                    크레딧 지급/차감 관리
                  </Button>
                )}
              </>
            );
          },
        },
        {
          key: 'isMarketingAgreed',
          lable: '마케팅 활용 동의 여부',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.account?.accountOptionalTerms ? 'true' : 'false',
          render: DetailSctionRenderer.BADGE({
            options: [
              {
                label: OPTIONS_CONST.agreed,
                key: 'true',
                badgeColor: 'lightGreen',
              },
              {
                label: OPTIONS_CONST.notAgreed,
                key: 'false',
                badgeColor: 'lightGray',
              },
            ],
          }),
        },
        {
          key: 'lastLoggedInAt',
          lable: '마지막 로그인 일시',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.account?.lastLoggedInAt || '',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
      ],
    ];
  }, [customerData?.status]);

  const suspendedContents = useCallback(() => {
    return [
      [
        {
          key: 'createdAt',
          lable: '가입 일시',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'signupSourceName',
          lable: '가입 경로',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.signupSource === null
              ? '에코야얼스'
              : data?.signupSource?.partnershipCompany?.name || '-',
        },
        {
          key: 'nick',
          lable: '닉네임',
          valueSetter: (data: CustomersDto | undefined) => data?.nick || '-',
        },
        {
          key: 'aId',
          lable: '회원 아이디',
          valueSetter: (data: CustomersDto | undefined) => data?.aId || '-',
        },
        {
          key: 'name',
          lable: '회원 이름',
          valueSetter: (data: CustomersDto | undefined) => data?.name || '-',
        },
        {
          key: 'businessName',
          lable: '사업체 명',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.customerBusinessInfo.businessName || '-',
        },
        {
          key: 'businessRegisterNumber',
          lable: '사업자등록번호',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.customerBusinessInfo.businessRegisterNumber || '-',
        },
        {
          key: 'openDate',
          lable: '개업일',
          valueSetter: (data: CustomersDto | undefined) =>
            dayjs(data?.customerBusinessInfo.openDate).format('YYYY-MM-DD') ||
            '-',
        },
        {
          key: 'reportCnt',
          lable: '블라인드 수',
          valueSetter: (data: CustomersDto | undefined) => {
            const commentCount = data?.communityCommentReportCnt || 0;
            const postingCount = data?.communityPostReportCnt || 0;
            return `${(commentCount + postingCount).toLocaleString()}회`;
          },
        },
      ],
      [
        {
          key: 'status',
          lable: '회원 상태',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.status || 'normal',
          render: (value: string) => {
            const status: CustomerStatus = value as CustomerStatus;
            return (
              <>
                <span>{CUSTOMER_STATUS_OPTIONS[status]}</span>
                {value !== 'withdrawal' && (
                  <Button
                    buttonColor="secondary"
                    buttonSize="xSmall"
                    onClick={() => setIsStatusDialogOpen(true)}
                  >
                    회원 상태 관리
                  </Button>
                )}
              </>
            );
          },
        },
        {
          key: 'suspendedAt',
          lable: '정지 일시',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.suspendHistory?.updatedAt || '-',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'updateReason',
          lable: '정지 사유',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.suspendHistory?.updateReason || '-',
        },
        {
          key: 'credit',
          lable: `보유 크레딧(${UNIT_STRINGS.CREDIT})`,
          valueSetter: (data: CustomersDto | undefined) =>
            data?.credit?.currentCredit.toLocaleString() || '0',
          render: (credit: string) => {
            return (
              <>
                <span>{`${credit}${UNIT_STRINGS.CREDIT}`}</span>
                {customerData?.status !== 'withdrawal' && (
                  <Button
                    buttonColor="secondary"
                    buttonSize="xSmall"
                    onClick={() => setIsCreditDialogOpen(true)}
                  >
                    크레딧 지급/차감 관리
                  </Button>
                )}
              </>
            );
          },
        },
        {
          key: 'isMarketingAgreed',
          lable: '마케팅 활용 동의 여부',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.account?.accountOptionalTerms ? 'true' : 'false',
          render: DetailSctionRenderer.BADGE({
            options: [
              {
                label: OPTIONS_CONST.agreed,
                key: 'true',
                badgeColor: 'lightGreen',
              },
              {
                label: OPTIONS_CONST.notAgreed,
                key: 'false',
                badgeColor: 'lightGray',
              },
            ],
          }),
        },
      ],
    ];
  }, [customerData?.status]);

  const withdrawalContents = useCallback(() => {
    return [
      [
        {
          key: 'withdrawalAt',
          lable: '탈퇴 일시',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.withdrawalHistory?.updatedAt || '-',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'nick',
          lable: '닉네임',
          valueSetter: (data: CustomersDto | undefined) => data?.nick || '-',
        },
      ],
      [
        {
          key: 'status',
          lable: '회원 상태',
          valueSetter: (data: CustomersDto | undefined) => data?.status || '',
          render: (value: string) => {
            const status: CustomerStatus = value as CustomerStatus;
            return (
              <Typo typoType="b16r" typoColor="gray6">
                {status ? CUSTOMER_STATUS_OPTIONS[status] : '-'}
              </Typo>
            );
          },
        },
        {
          key: 'updateReason',
          lable: '탈퇴 사유',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.withdrawalHistory?.updateReason || '-',
        },
        {
          key: 'administrator',
          lable: '담당 관리자',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.withdrawalHistory?.administrator?.name || '-',
        },
      ],
    ];
  }, []);

  const getContents = useCallback((): DetailSectionContent<
    CustomersDto | undefined
  >[][] => {
    if (customerData?.status === 'withdrawal') return withdrawalContents();
    if (customerData?.status === 'suspended') return suspendedContents();
    if (customerData?.status === 'normal') return normalContents();
    return normalContents();
  }, [normalContents, suspendedContents, withdrawalContents, customerData]);

  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(-1)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <StatusManageDialog
        submitCallback={refetchCustomerData}
        customer={customerData}
        isOpen={isStatusDialogOpen}
        setOpen={setIsStatusDialogOpen}
      />
      <CreditManageDialog
        submitCallback={refetchCustomerData}
        customer={customerData}
        isOpen={isCreditDialogOpen}
        setOpen={setIsCreditDialogOpen}
      />
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.businessCustomersManagement,
          NAV_CONST.businessCustomersDetail,
        ]}
        pageTitle={NAV_CONST.businessCustomersDetail}
      />
      <div className="mb-24 flex flex-col gap-6">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                기본 정보
              </span>
            </div>
          }
          data={customerData}
          contents={getContents()}
        />
        <div className="flex flex-col">
          <SectionCard
            cardTitle="추가 정보"
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <CustomerDetailTabTable customerId={id!} />
          </SectionCard>
        </div>
      </div>
      {renderBottomFixedContent()}
    </>
  );
}

export default BusinessCustomerDetailPage;
